import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/site/Seo";
import JsonLd from "@/components/site/JsonLd";
import BookCall from "@/components/site/BookCall";
import { trackEvent } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Resources() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    axios.get(`${API}/insights`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const categories = ["All", ...Array.from(new Set(items.map((i) => i.category)))];
  const filtered = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <>
      <Seo
        title="Resources & Insights | Adobe Marketo Engage, Salesforce, MOPs — Xadentra"
        description="Field notes from senior marketing operators on Adobe Marketo Engage, Salesforce CRM, marketing operations, campaign operations, demand generation, reporting, and martech strategy."
        path="/resources"
      />
      <JsonLd
        id="resources-breadcrumb"
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://xadentra.com/" },
            { "@type": "ListItem", position: 2, name: "Resources", item: "https://xadentra.com/resources" },
          ],
        }}
      />

      <section className="bg-[#07111F] text-white relative overflow-hidden">
        <div className="xa-blob" style={{ width: 480, height: 480, background: "#6D4DFF", top: -180, left: -100, opacity: 0.5 }} />
        <div className="xa-blob" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -120, right: -120, opacity: 0.32 }} />
        <div className="absolute inset-0 xa-grid-dark opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32 relative">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF]">Resources & Insights</div>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.1] sm:leading-[1.05] lg:leading-[0.98] max-w-5xl">
            Field notes from the work.
          </h1>
          <p className="mt-8 text-lg text-white/70 max-w-3xl leading-relaxed">
            Senior marketing operators on Adobe Marketo Engage, Salesforce, lifecycle programs, ABM, and the data
            problems that keep RevOps leaders awake.
          </p>
        </div>
      </section>

      <section className="bg-[#F7F9FC] border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="flex flex-wrap gap-2" data-testid="resources-filter-bar">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => { setFilter(c); trackEvent("resource_filter", { category: c }); }}
                data-testid={`resources-filter-${c.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                className={`px-4 py-2 text-xs font-mono uppercase tracking-widest rounded-full border transition-colors ${
                  filter === c
                    ? "bg-[#07111F] text-white border-[#07111F]"
                    : "bg-white text-[#07111F] border-[#E5E7EB] hover:border-[#07111F]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
          {loading ? (
            <div className="text-[#6B7280]">Loading resources…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((p, i) => (
                <Link
                  key={p.slug}
                  to={`/resources/${p.slug}`}
                  data-testid={`resource-card-${i}`}
                  onClick={() => trackEvent("resource_view", { slug: p.slug })}
                  className="xa-lift bg-white border border-[#E5E7EB] rounded-2xl p-7 hover:border-[#6D4DFF]/40 cursor-pointer group flex flex-col"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">{p.category}</div>
                  <h2 className="font-display mt-4 text-xl font-semibold leading-snug text-[#07111F] group-hover:text-[#6D4DFF] transition-colors">
                    {p.title}
                  </h2>
                  <p className="mt-3 text-sm text-[#6B7280] leading-relaxed flex-1">{p.excerpt}</p>
                  <div className="mt-7 flex items-center justify-between text-xs text-[#6B7280] border-t border-[#E5E7EB] pt-4">
                    <span>{new Date(p.date).toLocaleDateString("en-US", { month: "short", year: "numeric" })} · {p.read_minutes} min read</span>
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform text-[#6D4DFF]" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-[#07111F] text-white relative overflow-hidden">
        <div className="xa-blob pointer-events-none" style={{ width: 460, height: 460, background: "#6D4DFF", top: -160, left: -120, opacity: 0.4 }} />
        <div className="xa-blob pointer-events-none" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -140, right: -100, opacity: 0.25 }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end relative">
          <div className="lg:col-span-8">
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">
              Want a deeper look at your Marketo or RevOps stack?
            </h2>
            <p className="mt-5 text-white/70 max-w-2xl">
              We turn the patterns in these articles into working systems. Book a working session
              with a senior specialist focused on your Marketo and RevOps setup.
            </p>
          </div>
          <BookCall source="resources_cta" className="lg:col-span-4 lg:justify-self-end w-fit">
            Book a meeting
          </BookCall>
        </div>
      </section>
    </>
  );
}
